import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, MessageCircle, Linkedin } from 'lucide-react';
import Header from '../components/Header';
import IntakeBanner from '../components/IntakeBanner';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import BookTrialButton from '../components/BookTrialButton';
import { articles, getArticleBySlug } from '../data/articles';

const WHATSAPP_LINK = `https://wa.me/60166291687?text=${encodeURIComponent(
  "Hi Kalah, I just read your article on Read Right Academy and I'd like to chat about how you can help my child."
)}`;

const Block = ({ block }) => {
  if (block.type === 'p') {
    return (
      <p className="text-lg text-gray-700 leading-relaxed mb-6">{block.text}</p>
    );
  }
  if (block.type === 'h2') {
    return (
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-12 mb-5">
        {block.text}
      </h2>
    );
  }
  if (block.type === 'list') {
    return (
      <ul className="mb-6 space-y-2 pl-4">
        {block.items.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-lg text-gray-700">
            <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[#3B4BA0] flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === 'pullquote') {
    return (
      <blockquote className="my-10 border-l-4 border-[#3B4BA0] bg-gradient-to-r from-blue-50 to-teal-50 px-6 py-5 rounded-r-xl">
        <p className="text-xl sm:text-2xl font-semibold text-[#2d3a7a] italic leading-snug">
          “{block.text}”
        </p>
      </blockquote>
    );
  }
  return null;
};

const ArticlePage = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (article) {
      document.title = `${article.title} | Read Right Academy`;
    }
    return () => {
      document.title = 'Read Right Academy';
    };
  }, [article]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug)
    .slice(0, 2);

  return (
    <>
      <IntakeBanner />
      <Header />
      <main className="bg-white pt-44">
        {/* Breadcrumb */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <Link
            to="/#articles"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#3B4BA0] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to all articles
          </Link>
        </div>

        {/* Title block */}
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-4">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs bg-blue-50 text-[#3B4BA0] px-3 py-1 rounded-full font-medium"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1
            data-testid="article-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-4"
          >
            {article.title}
          </h1>

          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-6">
            {article.subtitle}
          </p>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500 border-b border-gray-100 pb-6 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#3B4BA0] to-teal-500 text-white flex items-center justify-center font-bold text-sm">
                K
              </div>
              <span className="font-semibold text-gray-700">
                Kalah K. Singam
              </span>
            </div>
            <span className="hidden sm:inline">·</span>
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {article.dateDisplay}
            </span>
            <span className="hidden sm:inline">·</span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {article.readMinutes} min read
            </span>
          </div>

          {/* Hero image */}
          <div className="rounded-2xl overflow-hidden shadow-lg mb-10 aspect-[4/3] sm:aspect-[16/10] bg-slate-100">
            <img
              src={article.heroImage}
              alt={article.heroAlt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Body */}
          <div data-testid="article-body" className="article-body">
            {article.body.map((block, idx) => (
              <Block key={idx} block={block} />
            ))}
          </div>

          {/* CTA box */}
          <div className="mt-14 mb-6 rounded-2xl border-2 border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  Curious if Read Right Academy is right for your child?
                </h3>
                <p className="text-gray-600">
                  Book a free 45-minute trial — a gentle observation to see how
                  we teach reading.
                </p>
              </div>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="article-whatsapp-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold px-6 py-3.5 rounded-xl shadow-md hover:shadow-lg transition-all whitespace-nowrap"
              >
                <MessageCircle className="h-5 w-5" />
                Chat on WhatsApp
              </a>
            </div>
          </div>

          {/* LinkedIn footer link */}
          <div className="text-center mt-10 mb-4 text-sm text-gray-500">
            Originally shared on{' '}
            <a
              href="https://www.linkedin.com/in/k-kalah-kulasingam-b1749569/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#3B4BA0] hover:underline"
            >
              <Linkedin className="h-4 w-4" /> Kalah&apos;s LinkedIn
            </a>
            . Follow for more weekly insights.
          </div>
        </article>

        {/* Related articles */}
        {relatedArticles.length > 0 && (
          <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 mb-24">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">
              Keep reading
            </h3>
            <div className="grid sm:grid-cols-2 gap-6">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/articles/${rel.slug}`}
                  data-testid={`related-article-${rel.slug}`}
                  className="group block rounded-2xl overflow-hidden border-2 border-gray-100 hover:border-[#3B4BA0] hover:shadow-lg transition-all bg-white"
                >
                  <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                    <img
                      src={rel.heroImage}
                      alt={rel.heroAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                      <Calendar className="h-3.5 w-3.5" />
                      {rel.dateDisplay}
                    </div>
                    <h4 className="font-bold text-gray-900 text-lg group-hover:text-[#3B4BA0] transition-colors leading-snug mb-2">
                      {rel.title}
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
                      {rel.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <WhatsAppButton />
      <BookTrialButton />
    </>
  );
};

export default ArticlePage;
